import { useCallback, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { userPromoRolesQueryKeys } from '~/hooks/apis/promo/useGetUserPromoRoles';

import {
  PromoRole,
  PromoRoleCreateProps,
  PromoRoleFilters,
  UsePromoRoleProps,
  addPromoUserToRole,
  createPromoRole,
  deletePromoRole,
  getPromoRoles,
  unassignPromoRole,
  updatePromoRole,
} from '~/services/PromoService';

/**
 * Sorts promo roles with system roles first, then by creation date (newest first)
 */
const sortPromoRoles = (roles: Array<PromoRole>): Array<PromoRole> => {
  return roles.sort((a, b) => {
    // System roles first
    if (a.isSystem && !b.isSystem) return -1;
    if (!a.isSystem && b.isSystem) return 1;

    // Then sort by creation date (newest first)
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
};

export type UsePromoRoleResult = {
  promoRoles: Array<PromoRole> | null;
  total: number;
  isLoading: boolean;
  error: Error | null;
  status: number | null;
  fetchPromoRoles: (filters?: PromoRoleFilters) => Promise<void>;
  createPromoRole: (role: PromoRoleCreateProps) => Promise<void>;
  updatePromoRole: (roleId: string, updates: Partial<PromoRole>) => Promise<void>;
  assignUserToRole: (promoRoleId: string, userId: string) => Promise<void>;
  unassignPromoRole: (promoRoleId: string, userId: string) => Promise<void>;
  deletePromoRole: (roleId: string) => Promise<void>;
  filters: PromoRoleFilters;
  setFilters: (filters: PromoRoleFilters) => void;
};

export function usePromoRoles({ initialFilters }: UsePromoRoleProps): UsePromoRoleResult {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [promoRoles, setPromoRoles] = useState<Array<PromoRole>>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [status, setStatus] = useState<number | null>(null);
  const [filters, setFilters] = useState<PromoRoleFilters>(initialFilters);

  const fetchPromoRoles = useCallback(
    async (newFilters?: PromoRoleFilters) => {
      const filtersToUse = newFilters || filters;
      setIsLoading(true);
      setError(null);

      try {
        const response = await getPromoRoles(filtersToUse);

        setPromoRoles(sortPromoRoles(response.result.roles));
        setTotal(response.result.total);
        setStatus(response.status);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch promo roles');
        setError(error);
        enqueueSnackbar(`Error fetching promo roles: ${error.message}`, { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [filters, enqueueSnackbar],
  );

  const createPromoRoleHandler = useCallback(
    async (role: PromoRoleCreateProps) => {
      try {
        const res = await createPromoRole(role);
        if (res.status >= 200 && res.status < 300) {
          enqueueSnackbar('Promo role created successfully', { variant: 'success' });
        } else {
          enqueueSnackbar(`Error creating promo role: ${JSON.stringify(res)}`, { variant: 'error' });
        }
        await fetchPromoRoles(filters);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create promo role');
        enqueueSnackbar(`Error creating promo role: ${error.message}`, { variant: 'error' });
      }
    },
    [fetchPromoRoles, filters, enqueueSnackbar],
  );

  const deletePromoRoleHandler = useCallback(async (roleId: string) => {
    try {
      const res = await deletePromoRole(roleId);
      if (res.status >= 200 && res.status < 300) {
        enqueueSnackbar(`Promo role deleted successfully`, { variant: 'success' });
        fetchPromoRoles(filters);
      } else {
        enqueueSnackbar(`Failed to delete promo role ${JSON.stringify(res.result)}`, { variant: 'error' });
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete promo role');
      enqueueSnackbar(`Error deleting promo role: ${error.message}`, { variant: 'error' });
    }
  }, []);

  const updatePromoRoleHandler = useCallback(
    async (roleId: string, updates: Partial<PromoRole>) => {
      try {
        const updatedRole = await updatePromoRole(roleId, updates);
        if (updatedRole.status >= 200 && updatedRole.status < 300) {
          enqueueSnackbar(`Promo role '${updatedRole.result.roleName}' updated successfully `, { variant: 'success' });
          await queryClient.invalidateQueries({ queryKey: userPromoRolesQueryKeys.all });
          await fetchPromoRoles(filters);
        } else {
          enqueueSnackbar(`Failed to update promo role ${JSON.stringify(updatedRole.result)}`, {
            variant: 'error',
          });
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update promo role');
        enqueueSnackbar(`Error updating promo role: ${error.message}`, { variant: 'error' });
      }
    },
    [fetchPromoRoles, filters, enqueueSnackbar, queryClient],
  );

  const assignUserToRoleHandler = useCallback(
    async (promoRoleId: string, userId: string) => {
      try {
        if (!promoRoleId) {
          enqueueSnackbar(`No promo role Id set`, { variant: 'error' });
        }

        if (!userId) {
          enqueueSnackbar(`No userId set`, { variant: 'error' });
        }
        const res = await addPromoUserToRole(promoRoleId, userId);

        if (res.status >= 200 && res.status < 300) {
          enqueueSnackbar(`Added promo user to role`, { variant: 'success' });
          await queryClient.invalidateQueries({ queryKey: userPromoRolesQueryKeys.detail(userId) });
          await fetchPromoRoles(filters);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to add promo user to role');
        enqueueSnackbar(`Error adding promo user to role: ${error?.message}`, { variant: 'error' });
      }
    },
    [fetchPromoRoles, filters, enqueueSnackbar, queryClient],
  );

  const unassignPromoRoleHandler = useCallback(
    async (roleId: string, userId: string) => {
      try {
        const res = await unassignPromoRole(roleId, userId);
        if (res.status >= 200 && res.status < 300) {
          enqueueSnackbar(`Removed promo user from user`, { variant: 'success' });
          await queryClient.invalidateQueries({ queryKey: userPromoRolesQueryKeys.detail(userId) });
          await fetchPromoRoles(filters);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to add promo user to role');
        enqueueSnackbar(`Error removing promo user from role: ${error.message}`, { variant: 'error' });
      }
    },
    [fetchPromoRoles, filters, enqueueSnackbar, queryClient],
  );

  return {
    promoRoles,
    total,
    isLoading,
    error,
    status,
    fetchPromoRoles,
    updatePromoRole: updatePromoRoleHandler,
    createPromoRole: createPromoRoleHandler,
    assignUserToRole: assignUserToRoleHandler,
    deletePromoRole: deletePromoRoleHandler,
    unassignPromoRole: unassignPromoRoleHandler,
    filters,
    setFilters,
  };
}
